import CancelIcon from '@mui/icons-material/CancelTwoTone';
import CheckCircleIcon from '@mui/icons-material/CheckCircleTwoTone';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumberTwoTone';
import EditIcon from '@mui/icons-material/EditTwoTone';
import { Alert } from '@mui/material';
import {
    Card,
    IconButton,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Location } from '../../api/types';
import Permission from '../../common/permissions';
import { makePermissionListSelector } from '../../selector/authenticationSelector';
import EditLocationDialog from './EditLocationDialog';
import { formatAddressLine } from '../../utils/addressLine';
const PREFIX = 'LocationInfo';

const classes = {
    root: `${PREFIX}-root`,
    successInfo: `${PREFIX}-successInfo`,
    errorInfo: `${PREFIX}-errorInfo`,
    neutralInfo: `${PREFIX}-neutralInfo`,
    icon: `${PREFIX}-icon`,
    idMatching: `${PREFIX}-idMatching`,
    cardHeaderAction: `${PREFIX}-cardHeaderAction`,
    editButton: `${PREFIX}-editButton`,
};

const StyledCard = styled(Card)(({ theme }) => ({
    [`&.${classes.root}`]: {
        height: '100%',
    },

    [`& .${classes.successInfo}`]: {
        color: theme.palette.success.main,
    },

    [`& .${classes.errorInfo}`]: {
        color: theme.palette.error.main,
    },

    [`& .${classes.neutralInfo}`]: {
        color: 'gray',
    },

    [`& .${classes.icon}`]: {
        verticalAlign: 'middle',
        paddingBottom: 2,
        paddingRight: theme.spacing(1),
    },

    [`& .${classes.cardHeaderAction}`]: {
        marginBottom: '-8px',
    },

    [`& .${classes.editButton}`]: {
        padding: '7px',
    },
}));

interface LocationInfoProps {
    location: Location;
}

const LocationInfo = (props: LocationInfoProps) => {
    const { t } = useTranslation();
    const permission = useSelector(makePermissionListSelector());
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);

    const canEditLocation =
        permission.includes(Permission.editLocations()) ||
        permission.includes(Permission.editLocation(props.location.id));

    return (
        <StyledCard className={'bg-background border-foreground border'}>
            <div className={'p-5 py-4 text-foreground flex flex-col gap-4'}>
                <header className={'flex flex-row'}>
                    <h2
                        className={
                            'truncate text-2xl text-foreground flex-grow'
                        }
                    >
                        {props.location.name}
                    </h2>
                    {!canEditLocation ? null : (
                        <IconButton
                            className={'text-foreground p-1'}
                            onClick={() => setEditDialogOpen(true)}
                            title={t('location_details.edit_location_info')}
                            children={<EditIcon />}
                            size={'large'}
                        />
                    )}
                </header>
                <section className={'text-sm'}>
                    <span>{formatAddressLine(props.location.address)}</span>
                    <div className={'mt-4'}>
                        <Typography
                            variant={'body1'}
                            className={
                                props.location.enabled
                                    ? classes.successInfo
                                    : classes.errorInfo
                            }
                            children={
                                props.location.enabled ? (
                                    <>
                                        <CheckCircleIcon
                                            className={classes.icon}
                                        />
                                        {t('location_details.status.active')}
                                    </>
                                ) : (
                                    <>
                                        <CancelIcon className={classes.icon} />
                                        {t(
                                            'location_details.status.not_active',
                                        )}
                                    </>
                                )
                            }
                        />
                        <Typography
                            variant={'body1'}
                            className={
                                props.location.status.bookable
                                    ? classes.successInfo
                                    : classes.errorInfo
                            }
                            children={
                                <>
                                    <ConfirmationNumberIcon
                                        className={classes.icon}
                                    />
                                    {props.location.status.bookable
                                        ? t('location_details.status.bookable')
                                        : t(
                                            'location_details.status.not_bookable',
                                        )}
                                </>
                            }
                        />
                    </div>
                    {props.location.status.notification && (
                        <Alert
                            severity={props.location.status.notification.type}
                        >
                            {props.location.status.notification.text}
                        </Alert>
                    )}
                </section>
            </div>
            <EditLocationDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                location={props.location}
            />
        </StyledCard>
    );
};

export default LocationInfo;
